import type { NextProps } from './optimizely.types';
import {
    createContext,
    useEffect,
    useState,
    useReducer,
    Dispatch
} from 'react';
import { useRouter } from 'next/router';
import triggerTracking from '@blueprint/tracking';
import { useUserSetOptimizelyOverrides } from './hooks/useUserSetOptimizelyOverrides';
import { writeOverridesToCookies } from './optimizelyOverrides';
import { asMap, OptimizelyStateMap, OptimizelyState } from './state';

export enum WITH_OPTIMIZELY_PAGE_PROP_NAME {
    STATE = '__OPTIMIZELY_STATE__'
}

export type WithOptimizelyPageProps = {
    [WITH_OPTIMIZELY_PAGE_PROP_NAME.STATE]?: OptimizelyState;
};

export type OptimizelyProps = {
    state?: OptimizelyState;
};

type ActivateState = Record<string, true>;
type Action = {
    type: 'activate';
    payload: { experiment: string; variation: string };
};

export const activateReducer: React.Reducer<ActivateState, Action> = (
    state,
    action
) => {
    if (action.type === 'activate' && !state[action.payload.experiment]) {
        trackActivate(action.payload.experiment, action.payload.variation);
        return { ...state, [action.payload.experiment]: true };
    }
    return state;
};

export const Context = createContext<
    OptimizelyStateMap & { loading: boolean; activate: Dispatch<Action> }
>({
    loading: true,
    experiments: {},
    features: {},
    activate: () => void 0
});

export const Provider: React.FC<
    React.PropsWithChildren<{ state?: OptimizelyState }>
> = ({ children, state: prefetchedState }) => {
    const [, dispatch] = useReducer(activateReducer, {});
    const [state, setState] = useState(asMap(prefetchedState ?? []));
    const [loading, setLoading] = useState(prefetchedState ? false : true);
    const userSetOverrides = useUserSetOptimizelyOverrides();
    const router = useRouter();

    useEffect(() => {
        const controller = new AbortController();

        async function fetchState() {
            setLoading(true);
            try {
                const resp = await fetch(
                    new URL(
                        '/api/experiments' + router.asPath,
                        window.location.origin
                    ).toString(),
                    {
                        credentials: 'include',
                        headers: {
                            cookie: document.cookie,
                            'user-agent': window.navigator.userAgent
                        },
                        signal: controller.signal
                    }
                );
                if (resp.ok) {
                    const respObj = await resp.json();
                    setState(asMap(respObj.state));
                    writeStateToStorage(respObj.state);
                }
            } catch (e) {
                console.log('datafile error', e);
                // do nothing
            } finally {
                setLoading(false);
            }
        }

        if (!prefetchedState) {
            const sessionState = getStateFromStorage();
            if (sessionState) {
                setState(sessionState);
                setLoading(false);
            } else if (router?.isReady && router?.asPath) {
                fetchState();
            }
        }
        return () => {
            controller.abort();
        };
    }, [prefetchedState, router?.asPath, router?.isReady]);

    useEffect(() => {
        if (userSetOverrides) {
            writeOverridesToCookies(userSetOverrides);
        }
        if (prefetchedState) {
            writeStateToStorage(prefetchedState);
        }
    }, [userSetOverrides, prefetchedState]);

    /* new relic */
    useEffect(() => {
        if (!loading && state.features) {
            const enabledFeatures = Object.keys(state.features);
            if (enabledFeatures?.length) {
                window.newrelic?.setCustomAttribute(
                    'enabledOptimizelyFeatures',
                    enabledFeatures.join('|')
                );
            }
        }
    }, [state.features, loading]);

    /* ContentSquare */
    useEffect(() => {
        function callback() {
            if (window.CS_CONF) {
                window.CS_CONF.integrations = window.CS_CONF.integrations || [];
                window.CS_CONF.integrations.push('Optimizely Full Stack');
            }
        }

        window._uxa = window._uxa || [];
        window._uxa.push(['afterPageView', callback]);
    }, []);

    const value = {
        experiments: state.experiments,
        features: state.features,
        loading,
        activate: dispatch
    };

    return <Context.Provider value={value}>{children}</Context.Provider>;
};

// https://www.typescriptlang.org/docs/handbook/2/conditional-types.html#distributive-conditional-types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DistributeWithOptimizelyPageProps<T> = T extends any
    ? T & WithOptimizelyPageProps
    : never;

/** Add state to pageProps using enum key */
export const addOptimizelyProps = <P extends NextProps = NextProps>(
    { state }: OptimizelyProps,
    pageProps: P
) => {
    const dupe = { ...pageProps } as DistributeWithOptimizelyPageProps<P>;
    if (state) dupe[WITH_OPTIMIZELY_PAGE_PROP_NAME['STATE']] = state;
    return dupe;
};

/**
 * Called on the client ONCE per activate dispatch event, per unique experiment
 * name
 */
function trackActivate(experiment: string, variation: string) {
    const isFlag = variation === 'true' || variation === 'false';

    // /* Optimizely activation */
    // if (navigator?.sendBeacon) {
    //     navigator.sendBeacon(
    //         window.location.origin + '/api/experiments/_activate/',
    //         experiment
    //     );
    // }

    /* GA event */
    triggerTracking<{
        taxonomy: {
            optimizely_experiment: string;
            optimizely_variation: string;
        };
    }>({
        event: 'GAEvent',
        eventCategory: isFlag ? 'optimizely_feature' : 'optimizely',
        eventLabel: variation,
        eventAction: experiment,
        taxonomy: {
            optimizely_experiment: experiment,
            optimizely_variation: variation
        }
    });

    /* ContentSquare */
    window._uxa = window._uxa || [];
    window._uxa.push([
        'trackDynamicVariable',
        { key: 'AB_OP_' + experiment, value: variation }
    ]);
}

const STORAGE_KEY = 'z_exp_state';

function getStateFromStorage(): OptimizelyStateMap | null {
    const state = window.sessionStorage.getItem(STORAGE_KEY);
    try {
        return state ? asMap(JSON.parse(state)) : null;
    } catch (e) {
        return null;
    }
}

function writeStateToStorage(state: OptimizelyState) {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// docs on the decsion strings generated by optimizely: https://support.optimizely.com/hc/en-us/articles/4410282918669 & https://support.optimizely.com/hc/en-us/articles/4410288174349
// docs on the web integration: https://www.optimizely.com/developers/labs/contentsquare-integration-web/
// and contentsq docs on the integration: https://docs.contentsquare.com/uxa-en/#general-principles
